import type { ReactNode } from 'react';
import { Button } from './Button';
import { uiCopy } from './copy';
import { HoldToConfirm } from './HoldToConfirm';
import { Modal } from './Modal';

/**
 * Yes or no, on a Modal. Destructive ones default to hold-to-confirm (800 ms), so a stray tap on
 * a court can not void a game.
 *
 *   <Confirm open={ask} title="End the game?" body="Final score 21 to 17."
 *     confirmLabel="Hold to end" destructive onConfirm={end} onCancel={() => setAsk(false)} />
 */
export interface ConfirmProps {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** true for 800 ms, a number for custom ms, false for a plain tap. Default: `destructive`. */
  hold?: boolean | number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function Confirm({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = uiCopy.confirm.cancel,
  destructive = false,
  hold,
  onConfirm,
  onCancel,
}: ConfirmProps) {
  const h = hold ?? destructive;
  const ms = typeof h === 'number' ? h : h ? 800 : 0;
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      actions={
        <>
          <Button onClick={onCancel}>{cancelLabel}</Button>
          {ms > 0 ? (
            <HoldToConfirm
              label={confirmLabel}
              ms={ms}
              variant={destructive ? 'danger' : 'primary'}
              onConfirm={onConfirm}
            />
          ) : (
            <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          )}
        </>
      }
    >
      {body}
    </Modal>
  );
}
